import { useApolloClient } from "@apollo/client";
import { CardContainer } from "@msys/ui";
import { Button, List } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import {
  ProfileListItem,
  ProfileListItemButton,
} from "../../../commons/ProfileListItem.js";
import { InvitationRow__ReceivedLinkingInvitationFragment } from "../../invitations/Invitations.generated.js";
import { OrganisationAvatar } from "../../organisations/OrganisationAvatar.js";
import { useRedeemLinkingInvitationTokenMutation } from "./PendingInvitationsBox.generated.js";

export function PendingInvitationsBox({
  receivedLinkingInvitations,
}: {
  receivedLinkingInvitations: InvitationRow__ReceivedLinkingInvitationFragment[];
}) {
  const { t } = useTranslate(["UserProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const [
    redeemLinkingInvitationToken,
    { loading: redeemingLinkingInvitation },
  ] = useRedeemLinkingInvitationTokenMutation({
    client,
    refetchQueries: [
      namedOperations.Query.CrmAll,
      namedOperations.Query.InvitationList,
    ],
    awaitRefetchQueries: true,
  });

  const handleRedeemLinkingInvitation = async (invitationToken: string) => {
    await redeemLinkingInvitationToken({
      variables: { token: invitationToken },
    });
    enqueueSnackbar(t("Invitation accepted", { ns: "UserProfile" }));
  };

  if (receivedLinkingInvitations.length === 0) return null;

  return (
    <CardContainer
      isInitiallyClosed={receivedLinkingInvitations.length === 0}
      itemCount={receivedLinkingInvitations.length}
      isExpandable
      title={t("Invitations to connect", {
        ns: "UserProfile",
      })}
    >
      <List disablePadding>
        {receivedLinkingInvitations.map((i, index, array) => {
          const avatar = <OrganisationAvatar size="s" organisationAvatar={i} />;
          const button = (
            <Button
              size="extra-small"
              variant="outlined"
              color="primary"
              onClick={async e => {
                e.preventDefault();
                e.stopPropagation();
                await handleRedeemLinkingInvitation(i.token);
              }}
              disabled={redeemingLinkingInvitation}
            >
              {t("Accept", {
                ns: "Global",
              })}
            </Button>
          );

          return !i.publicProfileSlug ? (
            <ProfileListItem
              key={i.token}
              size="s"
              avatar={avatar}
              primary={i.title}
              ActionButton={button}
              listItemProps={{
                divider: index < array.length - 1,
              }}
            />
          ) : (
            <ProfileListItemButton
              size="s"
              key={i.token}
              avatar={avatar}
              primary={i.title}
              ActionButton={button}
              listItemProps={{
                divider: index < array.length - 1,
                component: Link,
                to: `/org/${i.publicProfileSlug}`,
              }}
            />
          );
        })}
      </List>
    </CardContainer>
  );
}
